<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import { api } from '@/api/client.js';
import AnalyticsChart from '@/components/charts/AnalyticsChart.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { MODULE_USAGE_EMPTY } from '@/utils/emptyStateCopy.js';

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');
function emptySummary() {
  return {
    activeModules: 0,
    measurableModules: 0,
    usedModules: 0,
    usagePct: null,
    coveragePct: null,
    minAccesses: 2,
    dedupWindowMinutes: 10,
    unmappedAccesses: 0,
  };
}
const summary = ref(emptySummary());
const modules = ref([]);

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  const result = await api.events.moduleUsage({
    appId: activeAppId.value, window: timeWindow.value, signal,
  });
  if (!result || result.ok !== true) throw new Error('Invalid module usage response');
  return result;
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    summary.value = { ...emptySummary(), ...(result.summary ?? {}) };
    modules.value = result.modules ?? [];
    ps.setState(summary.value.activeModules === 0 ? 'empty' : 'data');
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load module usage",
      detail: err?.message ?? '',
    });
  },
});

// El catálogo es tenant-scoped: no conservar filas del tenant anterior
// mientras llega la nueva respuesta.
watch(() => tenant.changeKey, () => {
  summary.value = emptySummary();
  modules.value = [];
  ps.setState('loading');
});

ps.setRetryHandler(() => tick());
defineExpose({ ps, refreshStatus });

const unused = computed(() => modules.value.filter((module) => module.status === 'unused'));
const unmeasured = computed(() => modules.value.filter((module) => module.status === 'unmeasured'));
const donutAria = computed(() => summary.value.usagePct == null
  ? 'Module usage is not measurable because no active modules have path rules'
  : `${summary.value.usagePct}% module usage, ${summary.value.usedModules} of ${summary.value.measurableModules} measurable modules used`);

// --- Reparto granular por módulo -------------------------------------
//
// El donut de arriba responde "cuántos módulos se usan"; este pastel
// responde "cómo se reparte el uso entre ellos". Solo entran los
// módulos `used`: un módulo sin uso no tiene porción que dibujar, y
// los `unused`/`unmeasured` ya tienen su propia sección de chips.
//
// Viridis es una rampa continua: sus stops contiguos son casi el mismo
// color. Se recorren salteados para que dos porciones vecinas nunca
// caigan en stops adyacentes.
const SLICE_COLORS = Object.freeze([
  'var(--viridis-0)', 'var(--viridis-4)', 'var(--viridis-2)', 'var(--viridis-6)',
  'var(--viridis-1)', 'var(--viridis-5)', 'var(--viridis-3)',
]);

const usedModules = computed(() => modules.value
  .filter((module) => module.status === 'used' && (module.accesses ?? 0) > 0)
  .sort((a, b) => b.accesses - a.accesses));

const usedAccesses = computed(() => usedModules.value
  .reduce((total, module) => total + module.accesses, 0));

const slices = computed(() => {
  const total = usedAccesses.value;
  if (total === 0) return [];

  return usedModules.value.map((module, index) => {
    const pct = (module.accesses / total) * 100;
    return {
      moduleKey: module.moduleKey,
      name: module.name,
      accesses: module.accesses,
      pct,
      // Una cifra decimal: con 19 módulos el redondeo a entero manda
      // varias porciones reales a "0%".
      pctLabel: `${pct.toFixed(1)}%`,
      color: SLICE_COLORS[index % SLICE_COLORS.length],
    };
  });
});

const pieAria = computed(() => `Share of access units across ${usedModules.value.length} used modules: `
  + slices.value.map((slice) => `${slice.name} ${slice.pctLabel}`).join(', '));
</script>

<template>
  <PanelCard
    title="Module usage"
    help-key="moduleUsage"
    :state="ps.state.value"
    :empty-headline="MODULE_USAGE_EMPTY.headline"
    :empty-body="MODULE_USAGE_EMPTY.body"
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <div class="module-usage">
      <div class="module-usage__top">
        <div class="module-usage__summary">
          <AnalyticsChart
            v-if="summary.measurableModules > 0"
            type="doughnut"
            :labels="['Used modules', 'Not used']"
            :datasets="[{ label: 'Modules', data: [summary.usedModules, summary.measurableModules - summary.usedModules] }]"
            :aria-label="donutAria"
            percentage
            :percentage-total="summary.measurableModules"
            :center-value="summary.usagePct == null ? '—' : `${summary.usagePct}%`"
            center-label="modules used"
            class="module-usage__donut-chart"
          />
          <div class="module-usage__metrics">
            <p class="module-usage__lead">
              <strong v-if="summary.usagePct == null">Not measurable</strong>
              <template v-else>
                <strong>{{ summary.usedModules }} of {{ summary.measurableModules }}</strong>
                measurable modules used
              </template>
            </p>
            <p>
              Catalog coverage: {{ summary.coveragePct ?? 0 }}%
              ({{ summary.measurableModules }} of {{ summary.activeModules }} active modules)
            </p>
            <p class="module-usage__hint">
              Used means at least {{ summary.minAccesses }} access units. Repeated activity is deduplicated in {{ summary.dedupWindowMinutes }}-minute windows.
            </p>
            <p
              v-if="summary.unmappedAccesses > 0"
              class="module-usage__unmapped"
            >
              {{ summary.unmappedAccesses }} access {{ summary.unmappedAccesses === 1 ? 'unit' : 'units' }} could not be mapped to a module.
            </p>
          </div>
        </div>

        <section class="module-usage__breakdown">
          <h3 class="module-usage__heading">
            Usage share per module
          </h3>
          <div
            v-if="slices.length"
            class="module-usage__breakdown-body"
          >
            <AnalyticsChart
              type="doughnut"
              :labels="slices.map((slice) => slice.name)"
              :datasets="[{ label: 'Access units', data: slices.map((slice) => slice.accesses), backgroundColor: slices.map((slice) => slice.color) }]"
              :aria-label="pieAria"
              percentage
              :percentage-total="usedAccesses"
              class="module-usage__donut-chart"
            />
            <ul class="module-usage__legend">
              <li
                v-for="slice in slices"
                :key="`legend-${slice.moduleKey}`"
                data-test="usage-legend-item"
              >
                <span
                  class="module-usage__swatch"
                  :style="{ background: slice.color }"
                  aria-hidden="true"
                />
                <span class="module-usage__legend-name">{{ slice.name }}</span>
                <span class="module-usage__legend-value">
                  {{ slice.pctLabel }} · {{ slice.accesses }}
                </span>
              </li>
            </ul>
          </div>
          <p
            v-else
            class="module-usage__hint"
          >
            No module reached the usage threshold in this window.
          </p>
        </section>
      </div>

      <section v-if="unused.length">
        <h3 class="module-usage__heading">
          Not used in this window
        </h3>
        <div class="module-usage__chips">
          <span
            v-for="module in unused"
            :key="module.moduleKey"
            class="module-usage__chip module-usage__chip--unused"
          >{{ module.name }}</span>
        </div>
      </section>

      <section v-if="unmeasured.length">
        <h3 class="module-usage__heading">
          Pending path classification
        </h3>
        <div class="module-usage__chips">
          <span
            v-for="module in unmeasured"
            :key="module.moduleKey"
            class="module-usage__chip"
          >{{ module.name }}</span>
        </div>
      </section>
    </div>

    <template #tableFallback>
      <table class="module-usage__table">
        <caption class="visually-hidden">
          Usage status and access units for active modules
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Module
            </th>
            <th scope="col">
              Status
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Access units
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="module in modules"
            :key="`table-${module.moduleKey}`"
          >
            <td>{{ module.name }}</td>
            <td>{{ module.status }}</td>
            <td class="numeric">
              {{ module.accesses ?? '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.module-usage {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

/* Izquierda: cuántos módulos se usan. Derecha: cómo se reparte ese
   uso. `minmax(0, …)` para que la leyenda pueda truncar en vez de
   estirar la grilla. */
.module-usage__top {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-6);
  align-items: start;
}

.module-usage__summary {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  padding: var(--space-4);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-xl);
  background: var(--bg-subtle);
}

.module-usage__breakdown {
  min-width: 0;
  padding: var(--space-4);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-xl);
}

.module-usage__donut-chart {
  flex: 0 0 11rem;
  width: 11rem;
}

.module-usage__breakdown-body {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.module-usage__pie {
  width: 9rem;
  height: 9rem;
  flex: 0 0 9rem;
}

.module-usage__legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: var(--space-1);
  min-width: 0;
  max-height: 14rem;
  overflow-y: auto;
}

.module-usage__legend li {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  font-size: var(--text-xs);
  color: var(--fg-secondary);
}

.module-usage__swatch {
  width: .625rem;
  height: .625rem;
  border-radius: var(--radius-sm);
  flex: none;
  align-self: center;
}

.module-usage__legend-name {
  color: var(--fg-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.module-usage__legend-value {
  margin-left: auto;
  font-feature-settings: "tnum";
  white-space: nowrap;
}

.module-usage__donut {
  width: 9rem;
  height: 9rem;
  flex: 0 0 9rem;
  display: grid;
  place-items: center;
  border-radius: var(--radius-full);
  background: conic-gradient(var(--success-icon) var(--usage-angle), var(--bg-subtle) 0);
}

.module-usage__donut-center {
  width: 6.5rem;
  height: 6.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-full);
  background: var(--bg-canvas);
  color: var(--fg-secondary);
  font-size: var(--text-xs);
}

.module-usage__donut-center strong {
  color: var(--fg-primary);
  font-size: var(--text-2xl);
  font-feature-settings: "tnum";
}

.module-usage__metrics p {
  margin: 0 0 var(--space-2);
}

.module-usage__lead {
  font-size: var(--text-lg);
  color: var(--fg-primary);
}

.module-usage__hint {
  color: var(--fg-tertiary);
  font-size: var(--text-xs);
}

.module-usage__unmapped {
  color: var(--warning-fg);
  font-size: var(--text-sm);
}

.module-usage__heading {
  margin: 0 0 var(--space-2);
  color: var(--fg-secondary);
  font-size: var(--text-sm);
  font-weight: var(--fw-semibold);
}

.module-usage__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.module-usage__chip {
  padding: var(--space-1) var(--space-2);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-full);
  background: var(--bg-subtle);
  color: var(--fg-secondary);
  font-size: var(--text-xs);
  overflow-wrap: anywhere;
  max-width: 100%;
}

.module-usage__chip--unused {
  border-color: var(--warning-border);
  color: var(--warning-fg);
}

.module-usage__table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.module-usage__table th,
.module-usage__table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
  overflow-wrap: anywhere;
}

.module-usage__table th {
  color: var(--fg-secondary);
  font-weight: var(--fw-medium);
}

.module-usage__table .numeric {
  text-align: right;
  font-feature-settings: "tnum";
}

@media (width <=1023px) {
  .module-usage__top {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (width <=639px) {
  .module-usage__summary {
    align-items: flex-start;
    flex-direction: column;
  }

  .module-usage__breakdown-body {
    align-items: flex-start;
    flex-direction: column;
  }

  .module-usage__legend {
    max-height: none;
    width: 100%;
  }
}
</style>
